import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Form } from '@formio/react';
import { ONBOARDAPIURL } from '../../../config';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SignatureModal from '../../../components/Billing/SignatureCap/SignatureModal';
import moment from 'moment';

const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    new Uint8Array(buffer).forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary);
};

const ClientSurveyForm = (props) => {
    const token = props.match.params.token.split('?')[0];
    const [data, setData] = useState(null);
    const [selectedForm, setSelectedForm] = useState(null);
    const [open, setOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [signatureModal, setSignatureModal] = useState(false);
    const [signCaptured, setSignCaptured] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setData(null);
        setSubmitted(false);
        setError('');
        axios.get(ONBOARDAPIURL + '/client-survey', { params: { token } })
            .then((response) => {
                if (cancelled) return;
                const tokenData = response.data.token;
                const entityData = JSON.parse(tokenData.entity_data);
                const companyLogo = response.data.companyLogo;
                const forms = (response.data.forms || []).map((form) => ({
                    ...form,
                    completed: Boolean(form.data),
                }));
                setData({
                    forms,
                    taskId: entityData.task_id,
                    projectId: entityData.project_id,
                    customerId: entityData.customer_id,
                    customerName: entityData.customer_name,
                    employeeId: tokenData.created_by,
                    employeeName: entityData.employee_name || response.data.employeeName,
                    companyName: entityData.company_name,
                    surveyTitle: entityData.survey_title || 'Client Survey',
                    signatureRequired: entityData.signature_required,
                    expiresAt: tokenData.expires_at,
                    companyLogo: companyLogo
                        ? `data:${companyLogo.type};base64,${arrayBufferToBase64(companyLogo.logo.data)}`
                        : null,
                });
            })
            .catch((requestError) => {
                if (cancelled) return;
                if (requestError.response?.status === 400 && requestError.response?.data?.error === 'Token already used') {
                    setSubmitted(true);
                    return;
                }
                setError(requestError.response?.data?.error || 'This survey link is unavailable. Contact the sender for a new link.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [token]);

    const saveForm = async (submission) => {
        if (!selectedForm) return;
        setSubmitting(true);
        setError('');
        try {
            await axios.post(ONBOARDAPIURL + '/client-survey', {
                token,
                form: { form_id: selectedForm.form_id, data: submission },
            }, { headers: { 'Content-Type': 'application/json' } });
            setData((previous) => ({
                ...previous,
                forms: previous.forms.map((form) => form.form_id === selectedForm.form_id
                    ? { ...form, completed: true, data: submission }
                    : form),
            }));
            setOpen(false);
            setSelectedForm(null);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Your answers could not be saved. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const submitSurvey = async () => {
        if (submitting || data?.forms.some((form) => !form.completed)) return;
        if (data.signatureRequired && !signCaptured) {
            setSignatureModal(true);
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await axios.post(ONBOARDAPIURL + '/client-survey/submit', { token }, {
                headers: { 'Content-Type': 'application/json' },
            });
            setSubmitted(true);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Your survey could not be submitted. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const getSchema = (form) => {
        try {
            return JSON.parse(form.translated_json_schema || form.json_schema || '{}');
        } catch (_) {
            return {};
        }
    };

    if (loading) {
        return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
    }

    const submissionConfirmation = (
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, border: '1px solid #ddd', textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#3FA7D6' }} />
            <Typography variant="h4" sx={{ mt: 2 }}>Survey submitted</Typography>
            <Typography sx={{ mt: 1, color: '#666' }}>Thank you. Your responses have been received. No further action is needed.</Typography>
        </Paper>
    );

    if (!data) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <Box sx={{ maxWidth: 680, mx: 'auto', p: 3, pt: 8 }}>
                    {submitted ? submissionConfirmation : <Alert severity="error">{error}</Alert>}
                </Box>
            </Box>
        );
    }

    const completedCount = data.forms.filter((form) => form.completed).length;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', color: '#333', pb: 8, '& .MuiButton-contained': { bgcolor: '#3FA7D6', '&:hover': { bgcolor: '#3595bf' } }, '& .MuiButton-outlined': { color: '#3FA7D6', borderColor: '#3FA7D6' } }}>
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #ddd', px: 3, py: 2 }}>
                <Box sx={{ maxWidth: 1000, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {data.companyLogo && <Box component="img" src={data.companyLogo} alt={`${data.companyName} logo`} sx={{ width: 52, height: 52, objectFit: 'contain', bgcolor: 'white', borderRadius: 1, p: 0.5 }} />}
                    <Box>
                        <Typography variant="overline" sx={{ color: '#3FA7D6' }}>CLIENT SURVEY</Typography>
                        <Typography variant="h6">{data.companyName}</Typography>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ maxWidth: 820, mx: 'auto', px: 2, pt: { xs: 4, sm: 7 } }}>
                <Typography variant="overline" sx={{ color: '#666', fontWeight: 700 }}>Requested by {data.employeeName || data.companyName}</Typography>
                <Typography variant="h3" sx={{ fontSize: { xs: 32, sm: 42 }, fontWeight: 700, mt: 1 }}>{data.surveyTitle}</Typography>
                <Typography sx={{ mt: 2, color: '#666', maxWidth: 650 }}>
                    Please complete the forms below. You can save each form and return to this page before submitting your survey.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 4, alignItems: 'center' }}>
                    <Chip icon={<AssignmentOutlinedIcon />} label={`${completedCount} of ${data.forms.length} forms complete`} sx={{ bgcolor: 'rgb(235, 248, 255)', color: '#333', '& .MuiChip-icon': { color: '#3FA7D6' } }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>Due {moment(data.expiresAt).format('MMMM Do, YYYY')}</Typography>
                </Stack>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {submitted ? (
                    submissionConfirmation
                ) : (
                    <>
                        <Stack spacing={1.5}>
                            {data.forms.map((form, index) => (
                                <Paper key={form.form_id} elevation={0} sx={{ border: '1px solid #ddd', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography sx={{ color: '#888', fontWeight: 700, minWidth: 28 }}>{String(index + 1).padStart(2, '0')}</Typography>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ fontSize: 17 }}>{form.form_name}</Typography>
                                        {form.form_description && <Typography variant="body2" sx={{ color: '#666' }}>{form.form_description}</Typography>}
                                    </Box>
                                    {form.completed && <CheckCircleIcon sx={{ color: '#3FA7D6' }} />}
                                    <Button variant={form.completed ? 'outlined' : 'contained'} onClick={() => { setSelectedForm(form); setOpen(true); }}>
                                        {form.completed ? 'Review' : 'Complete'}
                                    </Button>
                                </Paper>
                            ))}
                        </Stack>
                        {data.forms.length === 0 && <Alert severity="warning" sx={{ mt: 2 }}>There are no forms attached to this survey. Contact the sender.</Alert>}
                        <Button
                            variant="contained"
                            size="large"
                            onClick={submitSurvey}
                            disabled={submitting || data.forms.length === 0 || data.forms.some((form) => !form.completed)}
                            sx={{ mt: 4, minWidth: 220 }}
                        >
                            {submitting ? 'Submitting...' : 'Submit survey'}
                        </Button>
                    </>
                )}
            </Box>
            <Dialog open={open} disableEscapeKeyDown maxWidth="md" fullWidth scroll="body">
                <DialogTitle>{selectedForm?.form_name}</DialogTitle>
                <DialogContent dividers>
                    {selectedForm && <Form form={getSchema(selectedForm)} submission={selectedForm.data} onSubmit={saveForm} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Close</Button>
                </DialogActions>
            </Dialog>
            <SignatureModal
                task={{ cusInfo: [{ cus_id: data.customerId, cus_name: data.customerName }], tas_pro_id: data.projectId, tas_id: data.taskId }}
                emp_id={data.employeeId}
                show={signatureModal}
                toggle={() => setSignatureModal((previous) => !previous)}
                captured={() => setSignCaptured(true)}
            />
        </Box>
    );
};

export default ClientSurveyForm;